import { useState, useCallback } from 'react';
import { addModuleToGroup, removeModuleFromGroup, reorderGroupModules } from '../services/modulesService';

/**
 * Centralized module ID mapping
 * Maps module codes to their numeric IDs in the database
 */
export const MODULE_ID_MAP = {
    'CONTACTS': 1,
    'CONVERSATIONS': 2,
    'MAP': 3,
    'AGENDA': 4,
    'EVENTS': 5,
    'PLACES': 6,
    'SUPPORT': 7,
    'WORK': 8,
    'OFFERS': 9
};

/**
 * Reverse mapping: numeric ID to code
 */
export const MODULE_CODE_MAP = {
    1: 'CONTACTS',
    2: 'CONVERSATIONS',
    3: 'MAP',
    4: 'AGENDA',
    5: 'EVENTS',
    6: 'PLACES',
    7: 'SUPPORT',
    8: 'WORK',
    9: 'OFFERS'
};

/**
 * Get numeric module ID from code
 * @param {string} code - Module code (e.g., 'CONTACTS')
 * @returns {number|undefined} - Numeric ID or undefined
 */
export const getModuleId = (code) => MODULE_ID_MAP[code];

/**
 * Get module code from numeric ID
 * @param {number} id - Numeric module ID
 * @returns {string|undefined} - Module code or undefined
 */
export const getModuleCode = (id) => MODULE_CODE_MAP[id];

/**
 * Hook for module drag and drop functionality
 * Centralizes all drag and drop state and handlers
 */
export const useModuleDragDrop = () => {
    // Drag state for modules within groups
    const [draggedGroupModule, setDraggedGroupModule] = useState(null); // { groupId, moduleIndex, module }
    const [dragOverGroupModuleItemId, setDragOverGroupModuleItemId] = useState(null);
    const [dragOverGroupId, setDragOverGroupId] = useState(null);

    // External drag state (from Modules page)
    const [isExternalModuleDrag, setIsExternalModuleDrag] = useState(false);

    /**
     * Handle drag start for a module within a group
     */
    const handleGroupModuleDragStart = useCallback((e, groupId, moduleIndex, module) => {
        e.stopPropagation();
        setDraggedGroupModule({ groupId, moduleIndex, module });

        // Set dataTransfer for cross-component drag
        e.dataTransfer.setData('moduleCode', module.code);
        e.dataTransfer.setData('sourceGroupId', groupId.toString());
        e.dataTransfer.setData('moduleId', (module.moduleid || module.id).toString());
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    /**
     * Handle drag over for a module within a group
     */
    const handleGroupModuleDragOver = useCallback((e, targetItemId, targetGroupId) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverGroupModuleItemId(targetItemId);

        // Set group drag over for visual feedback when dragging from different group
        if (draggedGroupModule && draggedGroupModule.groupId != targetGroupId) {
            setDragOverGroupId(targetGroupId);
        }

        // Detect external drag (from Modules page)
        const hasModuleData = e.dataTransfer.types.some(t => t.toLowerCase() === 'modulecode');
        if (!draggedGroupModule && hasModuleData) {
            setIsExternalModuleDrag(true);
            setDragOverGroupId(targetGroupId);
        }
    }, [draggedGroupModule]);

    /**
     * Reset all drag states
     */
    const handleGroupModuleDragEnd = useCallback(() => {
        setDraggedGroupModule(null);
        setDragOverGroupModuleItemId(null);
        setDragOverGroupId(null);
        setIsExternalModuleDrag(false);
    }, []);

    /**
     * Handle drop on a group - move module between groups
     * @param {Event} e - Drop event
     * @param {number} targetGroupId - Target group ID
     * @param {Function} loadGroups - Function to reload groups data
     * @returns {Promise<boolean>} - True if handled, false otherwise
     */
    const handleDropOnGroup = useCallback(async (e, targetGroupId, loadGroups) => {
        e.preventDefault();
        e.stopPropagation();

        // Handle module being dragged from within a group
        if (draggedGroupModule) {
            const sourceGroupId = draggedGroupModule.groupId;
            const moduleCode = draggedGroupModule.module.code;
            const numericModuleId = MODULE_ID_MAP[moduleCode];

            // Don't do anything if dropping on same group
            if (sourceGroupId == targetGroupId) {
                handleGroupModuleDragEnd();
                return true;
            }

            if (numericModuleId) {
                try {
                    await removeModuleFromGroup(sourceGroupId, numericModuleId);
                    await addModuleToGroup(targetGroupId, numericModuleId);
                    await loadGroups();
                    window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));
                } catch (error) {
                    console.error('Error moving module between groups:', error);
                }
            }

            handleGroupModuleDragEnd();
            return true;
        }

        // Handle external drag (via dataTransfer)
        const moduleCode = e.dataTransfer.getData('moduleCode');
        const sourceGroupId = e.dataTransfer.getData('sourceGroupId') || e.dataTransfer.getData('groupId');

        if (moduleCode) {
            const numericModuleId = MODULE_ID_MAP[moduleCode];

            if (numericModuleId) {
                // Don't do anything if dropping on same group
                if (sourceGroupId && parseInt(sourceGroupId) == targetGroupId) {
                    handleGroupModuleDragEnd();
                    return true;
                }

                try {
                    if (sourceGroupId) {
                        await removeModuleFromGroup(parseInt(sourceGroupId), numericModuleId);
                    }
                    await addModuleToGroup(targetGroupId, numericModuleId);
                    await loadGroups();
                    window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));
                } catch (error) {
                    console.error('Error moving module from external source:', error);
                }
            }
            handleGroupModuleDragEnd();
            return true;
        }

        return false;
    }, [draggedGroupModule, handleGroupModuleDragEnd]);

    /**
     * Handle reordering modules within the same group
     * @param {number} targetGroupId - Group ID
     * @param {Object} targetModule - Target module being dropped on
     * @param {Array} groupModules - Current modules in the group
     * @param {Function} loadGroups - Function to reload groups data
     */
    const handleReorderInGroup = useCallback(async (targetGroupId, targetModule, groupModules, loadGroups) => {
        if (!draggedGroupModule) return;

        const sourceGroupId = draggedGroupModule.groupId;
        const moduleCode = draggedGroupModule.module.code;

        // If dropping on a different group, move the module
        if (sourceGroupId != targetGroupId) {
            const numericModuleId = MODULE_ID_MAP[moduleCode];
            if (numericModuleId) {
                try {
                    await removeModuleFromGroup(sourceGroupId, numericModuleId);
                    await addModuleToGroup(targetGroupId, numericModuleId);
                    await loadGroups();
                    window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));
                } catch (error) {
                    console.error('Error moving module to different group:', error);
                }
            }
            handleGroupModuleDragEnd();
            return;
        }

        // Reorder within same group
        if (draggedGroupModule.module.itemid !== targetModule.itemid) {
            const draggedItemId = draggedGroupModule.module.itemid;
            const targetItemId = targetModule.itemid;

            const currentOrder = groupModules.map(m => m.itemid);
            const draggedIndex = currentOrder.indexOf(draggedItemId);
            const targetIndex = currentOrder.indexOf(targetItemId);

            if (draggedIndex !== -1 && targetIndex !== -1) {
                currentOrder.splice(draggedIndex, 1);
                currentOrder.splice(targetIndex, 0, draggedItemId);

                try {
                    await reorderGroupModules(targetGroupId, currentOrder);
                    await loadGroups();
                    window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));
                } catch (error) {
                    console.error('Error reordering modules:', error);
                }
            }
        }

        handleGroupModuleDragEnd();
    }, [draggedGroupModule, handleGroupModuleDragEnd]);

    return {
        // State
        draggedGroupModule,
        dragOverGroupModuleItemId,
        dragOverGroupId,
        isExternalModuleDrag,

        // Setters (for external use)
        setDragOverGroupId,
        setIsExternalModuleDrag,

        // Handlers
        handleGroupModuleDragStart,
        handleGroupModuleDragOver,
        handleGroupModuleDragEnd,
        handleDropOnGroup,
        handleReorderInGroup
    };
};

export default useModuleDragDrop;
